import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import LogInModal from 'flarum/forum/components/LogInModal';
import ConversationDirectory from './ConversationDirectory';
import MessagingEmptyState from './MessagingEmptyState';
import MessagesConversationHeader from './MessagesConversationHeader.js';
import MessagesComposeButton from './MessagesComposeButton.js';
import MessagesDiscoveryResults from './MessagesDiscoveryResults.js';
import discoverSources, { productMode } from '../utils/discoverSources.js';
import directConversationPaneStatus from '../utils/directConversationPaneStatus.js';
import { createPeopleSearchController, findUsersByQuery, MIN_REMOTE_USER_QUERY_LENGTH } from '../utils/peopleSearch.js';
import { collectFollowedUsers, suggestPeople } from '../utils/suggestPeople.js';
import flattenDiscoveryOptions from '../utils/flattenDiscoveryOptions.js';
import resolveConversationBackHref from '../utils/resolveConversationBackHref.js';

/** Additive V2 provider presentation contract (shell → providers). */
export const MESSAGES_PRESENTATION_VERSION = 2;
export default class MessagesPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'App--messages';
    // Directory is always the canonical "all" view — no visible filter strip.
    this.filter = 'all';
    this.query = '';
    this.consumedDraftKey = null;
    this.searchFocused = false;
    this.people = [];
    this.peopleStatus = 'idle';
    this.activeDiscoveryIndex = -1;
    this.peopleSearch = null;
    const state = app.flatrateMessagingState;
    if (state) {
      state.setFilter('all');
      state.setQuery('');
    }
    this.normalizeDirectoryFilterParam();
    this.syncDirectSelection();
    this.initPeopleSearch();
    if (app.session.user && app.flatrateMessaging) {
      Promise.resolve(app.flatrateMessaging.refresh()).then(() => {
        this.syncDirectSelection();
        m.redraw();
      });
    }
  }
  onremove() {
    if (this.peopleSearch) {
      this.peopleSearch.cancel();
    }
  }
  initPeopleSearch() {
    const actorId = app.session.user ? app.session.user.id() : null;
    this.peopleSearch = createPeopleSearchController({
      actorId,
      findUsers: q => findUsersByQuery(app, q)
    });
  }
  onbeforeupdate() {
    this.normalizeDirectoryFilterParam();
    this.syncDirectSelection();
  }

  /**
   * Strip stale ?filter= from /messages so an invisible filter cannot linger.
   */
  normalizeDirectoryFilterParam() {
    if (this.selectedFromRoute()) {
      return;
    }
    if (!m.route.param('filter')) {
      return;
    }
    const params = {
      ...m.route.param()
    };
    delete params.filter;
    m.route.set(app.route('flatrate-messaging.index', params), null, {
      replace: true
    });
  }
  syncDirectSelection() {
    const state = app.flatrateMessagingState;
    if (!state) {
      return;
    }
    if (!app.session.user) {
      if (state.selection.status !== 'idle') {
        state.clearDirectSelection();
      }
      return;
    }
    const selected = this.selectedFromRoute();
    if (!selected || selected.kind !== 'direct') {
      if (state.selection.kind === 'direct') {
        state.clearDirectSelection();
      }
      return;
    }
    const result = state.ensureDirectSelection(selected.key);
    if (result && typeof result.then === 'function') {
      result.then(() => m.redraw());
    }
  }
  view() {
    const selected = this.selectedFromRoute();
    const viewing = !!selected;
    const guest = !app.session.user;
    const sources = app.flatrateMessaging ? app.flatrateMessaging.sources() : {
      live: null,
      direct: null
    };
    const mode = productMode(sources);
    const state = app.flatrateMessagingState;
    const conversations = guest || mode === 'unavailable' ? [] : state ? state.visibleConversations() : [];
    return m("div", {
      className: 'MessagesPage MessagesShell' + (viewing ? ' viewing-conversation' : '')
    }, m(MessagesComposeButton, {
      mobilePinned: true
    }), m("aside", {
      className: "MessagesPage-directory MessagesDirectoryPane"
    }, this.directoryView({
      guest,
      mode,
      sources,
      state,
      conversations,
      selected
    })), m("section", {
      className: "MessagesPage-conversation MessagesConversationPane"
    }, this.conversationView({
      guest,
      mode,
      sources,
      state,
      selected
    })));
  }
  directoryView(_ref) {
    let {
      guest,
      mode,
      sources,
      state,
      conversations,
      selected
    } = _ref;
    if (guest) {
      return m("div", {
        className: "MessagesPage-status"
      }, m("h1", {
        className: "MessagesPage-title visually-hidden"
      }, app.translator.trans('flatrate-messaging-ui.forum.page.title')), m("p", null, app.translator.trans('flatrate-messaging-ui.forum.page.sign_in_required')), m(Button, {
        className: "Button Button--primary",
        onclick: () => app.modal.show(LogInModal)
      }, app.translator.trans('flatrate-messaging-ui.forum.page.sign_in')));
    }
    if (mode === 'unavailable') {
      return m("div", {
        className: "MessagesPage-status"
      }, m("h1", {
        className: "MessagesPage-title visually-hidden"
      }, app.translator.trans('flatrate-messaging-ui.forum.page.title')), m("p", null, app.translator.trans('flatrate-messaging-ui.forum.page.unavailable')));
    }
    const hasError = !!(state && (state.errors.live || state.errors.direct));
    const oncompose = sources.direct ? () => {
      if (app.flatrateMessaging && typeof app.flatrateMessaging.composeDirect === 'function') {
        app.flatrateMessaging.composeDirect();
      }
    } : null;
    const discoveryActive = this.isDiscoveryActive();
    const discoveryPeople = this.discoveryPeople(state);
    const discoveryConversations = conversations;
    const options = flattenDiscoveryOptions(discoveryConversations, discoveryPeople);
    const showDiscoveryEmpty = discoveryActive && String(this.query || '').trim().length > 0 && discoveryConversations.length === 0 && discoveryPeople.length === 0 && this.peopleStatus !== 'loading';
    return m("div", null, m("h1", {
      className: "MessagesPage-title visually-hidden"
    }, app.translator.trans('flatrate-messaging-ui.forum.page.title')), m("div", {
      className: "MessagesPage-search"
    }, m("i", {
      className: "fas fa-search MessagesPage-searchIcon",
      "aria-hidden": "true"
    }), m("input", {
      className: "FormControl MessagesPage-searchInput",
      type: "search",
      role: "combobox",
      "aria-label": app.translator.trans('flatrate-messaging-ui.forum.page.search_aria'),
      "aria-expanded": discoveryActive ? 'true' : 'false',
      "aria-controls": "messages-discovery-results",
      "aria-autocomplete": "list",
      "aria-activedescendant": this.activeDiscoveryIndex >= 0 ? `messages-discovery-option-${this.activeDiscoveryIndex}` : null,
      placeholder: app.translator.trans('flatrate-messaging-ui.forum.page.search_placeholder'),
      value: this.query,
      onfocus: () => {
        this.searchFocused = true;
        m.redraw();
      },
      onblur: () => {
        // Delay so mousedown on a result can fire first.
        setTimeout(() => {
          this.searchFocused = false;
          this.activeDiscoveryIndex = -1;
          m.redraw();
        }, 150);
      },
      oninput: e => this.onSearchInput(e.target.value, state),
      onkeydown: e => this.onSearchKeydown(e, options)
    })), hasError ? m("div", {
      className: "MessagesPage-error"
    }, app.translator.trans('flatrate-messaging-ui.forum.page.load_error')) : null, state && state.loading ? m(LoadingIndicator, null) : null, m("div", {
      id: "messages-discovery-results"
    }, discoveryActive ? m(MessagesDiscoveryResults, {
      conversations: discoveryConversations,
      people: discoveryPeople,
      selected: selected,
      peopleHeading: String(this.query || '').trim() ? app.translator.trans('flatrate-messaging-ui.forum.page.people_heading') : app.translator.trans('flatrate-messaging-ui.forum.page.suggested_people_heading'),
      conversationsHeading: app.translator.trans('flatrate-messaging-ui.forum.page.conversations_heading'),
      emptyLabel: app.translator.trans('flatrate-messaging-ui.forum.page.discovery_empty'),
      loadingPeople: this.peopleStatus === 'loading',
      activeIndex: this.activeDiscoveryIndex,
      showEmpty: showDiscoveryEmpty,
      onActivateConversation: conversation => this.activateConversation(conversation),
      onActivatePerson: person => this.activatePerson(person)
    }) : !state?.loading && conversations.length === 0 ? m(MessagingEmptyState, {
      oncompose: oncompose
    }) : m(ConversationDirectory, {
      conversations: conversations,
      selected: selected
    })));
  }
  isDiscoveryActive() {
    return this.searchFocused || String(this.query || '').trim().length > 0;
  }
  discoveryPeople(state) {
    const q = String(this.query || '').trim();
    if (q.length >= MIN_REMOTE_USER_QUERY_LENGTH) {
      return this.people;
    }
    if (!this.searchFocused && !q) {
      return [];
    }
    // Focused + empty (or short query): bounded suggestions.
    const actorId = app.session.user ? app.session.user.id() : null;
    const allConversations = state?.conversations || [];
    return suggestPeople({
      conversations: allConversations,
      followedUsers: collectFollowedUsers(app),
      actorId
    });
  }
  onSearchInput(value, state) {
    this.query = value;
    this.activeDiscoveryIndex = -1;
    if (state) state.setQuery(this.query);
    if (this.peopleSearch) {
      this.peopleSearch.schedule(this.query, result => {
        this.people = result.users || [];
        this.peopleStatus = result.status;
        m.redraw();
      });
    }
  }
  onSearchKeydown(e, options) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.clearDiscovery();
      return;
    }
    if (!options.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeDiscoveryIndex = Math.min(this.activeDiscoveryIndex + 1, options.length - 1);
      if (this.activeDiscoveryIndex < 0) this.activeDiscoveryIndex = 0;
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeDiscoveryIndex = Math.max(this.activeDiscoveryIndex - 1, 0);
      return;
    }
    if (e.key === 'Enter' && this.activeDiscoveryIndex >= 0) {
      e.preventDefault();
      const option = options[this.activeDiscoveryIndex];
      if (!option) return;
      if (option.type === 'conversation') {
        this.activateConversation(option.conversation);
      } else if (option.type === 'person') {
        this.activatePerson(option.person);
      }
    }
  }
  clearDiscovery() {
    this.query = '';
    this.people = [];
    this.peopleStatus = 'idle';
    this.activeDiscoveryIndex = -1;
    this.searchFocused = false;
    if (app.flatrateMessagingState) {
      app.flatrateMessagingState.setQuery('');
    }
    if (this.peopleSearch) {
      this.peopleSearch.cancel();
    }
    m.redraw();
  }
  activateConversation(conversation) {
    if (!conversation || !app.flatrateMessaging) return;
    this.clearDiscovery();
    app.flatrateMessaging.openConversation(conversation.kind, conversation.sourceId || conversation.key);
  }
  activatePerson(person) {
    if (!person?.user || !app.flatrateMessaging) return;
    this.clearDiscovery();
    Promise.resolve(app.flatrateMessaging.openDirectToUser(person.user)).then(() => m.redraw());
  }
  conversationView(_ref2) {
    let {
      guest,
      mode,
      sources,
      state,
      selected
    } = _ref2;
    if (guest || mode === 'unavailable' || !selected) {
      return m("div", {
        className: "MessagesPage-conversationPane"
      }, app.translator.trans('flatrate-messaging-ui.forum.page.select_prompt'));
    }
    const conversation = state && state.findBySelection(selected.kind, selected.key) || {
      kind: selected.kind,
      sourceId: selected.key,
      id: `${selected.kind}:${selected.key}`,
      title: selected.kind === 'live' && selected.key === 'community-general-live' ? 'FlatRate.wiki Live' : selected.key
    };
    if (selected.kind === 'direct') {
      const paneStatus = directConversationPaneStatus(state?.selection, selected.key);
      if (paneStatus === 'loading' || paneStatus === 'idle') {
        return this.conversationChrome(m(LoadingIndicator, null), {
          kind: 'direct',
          conversation
        });
      }
      if (paneStatus === 'not-found') {
        return this.conversationChrome(m("div", {
          className: "MessagesPage-status"
        }, app.translator.trans('flatrate-messaging-ui.forum.page.conversation_unavailable')), {
          kind: 'direct',
          conversation
        });
      }
      if (paneStatus === 'error') {
        return this.conversationChrome(m("div", {
          className: "MessagesPage-status"
        }, m("p", null, app.translator.trans('flatrate-messaging-ui.forum.page.conversation_error')), m(Button, {
          className: "Button",
          onclick: () => {
            if (!state) return;
            Promise.resolve(state.resolveDirectSelection(selected.key)).then(() => m.redraw());
          }
        }, app.translator.trans('flatrate-messaging-ui.forum.page.conversation_retry'))), {
          kind: 'direct',
          conversation
        });
      }
    }
    const provider = selected.kind === 'live' ? sources.live : sources.direct;
    const draftKey = `${selected.kind}:${selected.key}`;
    let initialDraft;
    if (state && this.consumedDraftKey !== draftKey) {
      initialDraft = state.takeInitialDraft(selected.key);
      this.consumedDraftKey = draftKey;
    }
    const context = {
      presentationVersion: MESSAGES_PRESENTATION_VERSION,
      initialDraft,
      conversation
    };
    let pane = null;
    if (provider && typeof provider.renderConversation === 'function') {
      pane = provider.renderConversation({
        key: selected.key,
        context
      });
    }
    return this.conversationChrome(pane, {
      kind: selected.kind,
      conversation
    });
  }
  conversationChrome(body, _temp) {
    let {
      kind = null,
      conversation = null
    } = _temp === void 0 ? {} : _temp;
    const paneClass = 'MessagesPage-conversationPane' + (kind ? ` MessagesPage-conversationPane--${kind}` : '');
    // Registry must be the Flarum app — calling discoverSources without app
    // yields null providers and drops Live overflow contributions.
    const sources = discoverSources(app);
    const provider = kind === 'live' ? sources.live : kind === 'direct' ? sources.direct : null;
    const selected = this.selectedFromRoute();
    const providerOverflowItems = provider && typeof provider.headerOverflowItems === 'function' ? provider.headerOverflowItems({
      key: selected?.key,
      conversation,
      kind
    }) : null;
    return m("div", {
      className: paneClass
    }, kind ? m(MessagesConversationHeader, {
      kind: kind,
      conversation: conversation,
      providerOverflowItems: providerOverflowItems,
      onBack: () => this.backFromConversation(kind, conversation),
      onAllMessages: () => this.backToList()
    }) : null, m("div", {
      className: "MessagesPage-conversationBody"
    }, m("div", {
      className: "MessagesProviderSurface"
    }, body)));
  }
  selectedFromRoute() {
    const roomKey = m.route.param('roomKey');
    const conversationId = m.route.param('conversationId');
    if (roomKey) {
      return {
        kind: 'live',
        key: String(roomKey)
      };
    }
    if (conversationId) {
      return {
        kind: 'direct',
        key: String(conversationId)
      };
    }
    return null;
  }
  backFromConversation(kind, conversation) {
    const href = resolveConversationBackHref(kind, conversation, app.flatRateBoardRoutes);
    if (href === '/messages') {
      this.backToList();
      return;
    }
    m.route.set(href);
  }
  backToList() {
    const params = {
      ...m.route.param()
    };
    delete params.roomKey;
    delete params.conversationId;
    // Returning to directory always clears filter so nothing remains silently applied.
    delete params.filter;
    this.filter = 'all';
    if (app.flatrateMessagingState) {
      app.flatrateMessagingState.setFilter('all');
    }
    m.route.set(app.route('flatrate-messaging.index', params));
  }
}
flarum.reg.add('flatrate-messaging-ui', 'forum/components/MessagesPage', MessagesPage);